import React, {useEffect, useState} from 'react'
import {Document, Font, Image, Page, PDFViewer, StyleSheet, Text, View} from '@react-pdf/renderer'
import {useParams} from 'react-router-dom'
import {IIncomeDocument} from './IncomeDocumentDetailPage'
import api from '../../utils/Api'
import {IncomeDocumentStateTypes, IncomeDocumentTypes} from '../../constants/Common'
import LDCLogo from '../../../_metronic/assets/ldc-logo.png'
import moment from 'moment'
import {calculateSummaryPriceInfo, IProductAccountSummary} from '../../utils/ProductHelper'
import PrintSignatureSection from './income-document-pdf/PrintSignatureSection'
import PrintProductSection from './income-document-pdf/PrintProductSection'
import PrintPaymentSection from './income-document-pdf/PrintPaymentSection'
import PrintPsSection from './income-document-pdf/PrintPSSection'
import PrintSummarySection from './income-document-pdf/PrintSummarySection'
import CombinedPaymentNotePDF from './income-document-pdf/CombinedPaymentNotePDF'
import {formatTaxIdWithBranch} from './income-document-pdf/partyBranch'
import {getIncomeDocumentPrintVolume, getTitleLabel} from '../../utils/Helper'

Font.register({
  family: 'DB-Helvethaica-X',
  fonts: [
    {
      src: require('../../../DB-Helvethaica-X.ttf'),
      fontWeight: 'normal',
    },
    {
      src: require('../../../DB-Helvethaica-X-bold.ttf'),
      fontWeight: 'bold',
    },
  ],
})

Font.register({
  family: 'FontAwesome',
  src: require('../../../fa-solid-900.ttf'),
})

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 12,
    padding: '24px 24px 8px',
    fontFamily: 'DB-Helvethaica-X',
  },
  section: { marginBottom: 12 },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  header: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#3F51B5',
    textAlign: 'right',
    marginBottom: 10
  },
  label: {
    fontWeight: 'bold'
  },
  table: {
    marginTop: 10
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: '#E0E7FF',
    fontWeight: 'bold',
    borderRadius: '4px',
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 4
  },
  summaryBox: {
    backgroundColor: '#F5F5F5',
    padding: 6,
    marginTop: 'auto',
  },
  rightBox: {
    textAlign: 'right'
  },
  signatureBox: {
    borderTopWidth: 1,
    borderColor: '#000',
    marginTop: 20,
    paddingTop: 6,
    textAlign: 'center'
  },
});

const IncomeDocumentPDFPage = () => {
  const { id } = useParams()
  const [document, setDocument] = useState<IIncomeDocument | undefined>(undefined)

  useEffect(() => {
    api.request(`account/income-document/${id}`, (res) => {
      setDocument(res)
    })
  }, [])

  if (!document) return <></>

  return (
    <div className={'mx-auto'} style={{height: 1182, width: 800}}>
      <PDFViewer showToolbar width='100%' height='100%'>
        {/* ใบรวมจ่ายมีแบบพิมพ์ของตัวเอง — แบบกลางเป็นของเอกสารฝั่งขาย (ดู CombinedPaymentNotePDF) */}
        {document.type === IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE ? (
          <CombinedPaymentNotePDF document={document} pageStyle={styles.page} />
        ) : (
          <IncomeDocumentPDF document={document} />
        )}
      </PDFViewer>
    </div>
  )
}

const IncomeDocumentPDF: React.FC<{document: IIncomeDocument}> = ({ document }) => {
  const [productAccountSummary, setProductAccountSummary] = useState<IProductAccountSummary>({} as  IProductAccountSummary)
  const hasIssueTaxInvoice = document.issueTaxInvoice
  const shouldRenderPayment = [IncomeDocumentTypes.DEPOSIT, IncomeDocumentTypes.RECEIPT, IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD, IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD].includes(document.type)
  const excludeField: string[] = []
  const vatType = document.vatType
  const printVolume = getIncomeDocumentPrintVolume(document)
  const totalPageCount = printVolume.copyPageTotal + printVolume.originalPageTotal;
  const totalPageList = Array.from({ length: totalPageCount }, (_, i) => i + 1);

  useEffect(() => {
    const productPriceSummary =  calculateSummaryPriceInfo(document)
    setProductAccountSummary(productPriceSummary)
  }, [document])

  const productPriceInfo = productAccountSummary.productPriceInfo

  if (!productPriceInfo) return (
    <Document title={`LDC-${document.code}`}>
      <Page size='A4' style={styles.page}>
      </Page>
    </Document>
  )

  const titleLabel = getTitleLabel(document)

  const _renderIconLabel = (icon: string, label: string) => {
    return (
      <View style={{display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '8px',}}>
        <Text style={{fontFamily: 'FontAwesome', fontSize: 9}}>{icon}</Text>
        <Text style={{}}>{label}</Text>
      </View>
    )
  }

  const _renderDocInfoRow = (label: string, value: any, labelWidth?: number) => {
    return (
      <View style={{display: 'flex', flexWrap: 'wrap', flexDirection: "row"}}>
        <Text style={{fontWeight: 'bold', width: labelWidth || 70}}>{`${label} :`}</Text>
        <Text style={{flex: 1}}>{value}</Text>
      </View>
    )
  }

  const DocHeadingSection = ({isOriginal}: {isOriginal: boolean}) => {
    return (
      <View style={{display: 'flex', flexDirection: "row", justifyContent: "space-between", alignItems: 'center'}}>
        <Image src={LDCLogo} style={{width: 100, height: 'auto'}} />
        <View style={{display: 'flex', flexDirection: "column", alignItems: 'flex-end', textAlign: 'right'}}>
          <Text style={{fontSize: 28, fontWeight: 'bold', color: '#3f51b5', textAlign: 'right'}}>
            {titleLabel}
          </Text>
          <Text style={{fontSize: 14, color: '#4c4c4c', textAlign: 'right', marginBottom: 10, marginTop: -6}}>
            {isOriginal ? '( ต้นฉบับ / original )' : '( สำเนา / copy )'}
          </Text>
        </View>
      </View>
    )
  }

  // เอกสารฝั่งรายจ่าย: บริษัทเป็นผู้ซื้อ คู่ค้าเป็นผู้ขาย — แบบนี้เดิมทำไว้ให้ฝั่งขาย ใบลดหนี้/เพิ่มหนี้/มัดจำ ฯลฯ
  // เลยพิมพ์ "ผู้ขาย = บริษัท" "ลูกค้า = คู่ค้า" และมีพนักงานขาย (30 ก.ย. 2569)
  const isExpenseDocument = String(document.type || '').startsWith('expense-')

  const DocContactSection = () => {
    const LeftSide = () => {
      const labelWidth = 42
      const customer = document.labCompany

      return (
        <View style={{flex: 1, paddingTop: '8px'}}>
          <View style={{display: 'flex', flexDirection: 'row'}}>
            <View style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', paddingRight: '4px'}}>
              {/* TODO:: Mock data */}
              {_renderDocInfoRow(isExpenseDocument ? 'ผู้ซื้อ' : 'ผู้ขาย', 'บริษัท แอลดีซี เด็นทัล จำกัด (มหาชน)', labelWidth)}
              {_renderDocInfoRow('ที่อยู่', '395-395/1 อาคารแอลดีซี ม.5 ถ.ศรีนครินทร์ ต.สำโรงเหนือ อ.เมืองสมุทรปราการ จ.สมุทรปราการ 10270', labelWidth)}
              {_renderDocInfoRow('เลขประจำตัวผู้เสียภาษีอากร', '0107557000080 (สำนักงานใหญ่)', 110)}
            </View>
            <View style={{width: '30%', display: 'flex', flexDirection: 'column', gap: '1px'}}>
              {/* TODO:: Mock data */}
              {_renderIconLabel('\uf095', '02-789-3050')}
              {_renderIconLabel('\uf0e0', 'contact@ldcdental.com')}
              {_renderIconLabel('\uf0ac', 'www.ldcdental.com')}
            </View>
          </View>
          <View style={{width: '100%', margin: '8px 0', borderTop: '1px solid #bfbfbf'}} />
          <View style={{display: 'flex', flexDirection: 'row'}}>
            <View style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', paddingRight: '4px'}}>
              {/* TODO:: Mock data */}
              {_renderDocInfoRow(isExpenseDocument ? 'ผู้ขาย' : 'ลูกค้า', customer?.name || '-', labelWidth)}
              {isExpenseDocument && _renderDocInfoRow('รหัส', customer?.code || '-', labelWidth)}
              {_renderDocInfoRow('ที่อยู่', customer?.fullAddress || '-', labelWidth)}
              {_renderDocInfoRow('เลขประจำตัวผู้เสียภาษีอากร', formatTaxIdWithBranch(customer?.taxId, customer?.branchCode), 110)}
              {_renderDocInfoRow('เรียนคุณ', customer?.contactName || '-', labelWidth)}
            </View>
            <View style={{width: '30%', display: 'flex', flexDirection: 'column', gap: '1px'}}>
              {/* TODO:: Mock data */}
              {_renderIconLabel('\uf095', customer.tel || '-')}
              {_renderIconLabel('\uf0e0', customer.email || '-')}
            </View>
          </View>
        </View>
      )
    }

    const RightSide = () => {
      const seller = document.seller

      return (
        <View style={{width: "33%"}}>
          <View style={{padding: '4px 8px', backgroundColor: '#e4e5fc', borderRadius: '.25rem'}}>
            {_renderDocInfoRow('เลขที่เอกสาร', document.code)}
            {_renderDocInfoRow('วันที่ออก', moment(document.date).format('DD/MM/YYYY'))}
            {document.type === IncomeDocumentTypes.QUOTATION && (
              _renderDocInfoRow(
                'วันที่ตอบรับ',
                document.state === IncomeDocumentStateTypes.ACCEPTED
                  ? moment(document.updatedAt).format('DD/MM/YYYY')
                  : '-'
              )
            )}
            {[IncomeDocumentTypes.QUOTATION, IncomeDocumentTypes.RECEIPT, IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD, IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD].includes(document.type) && (
              _renderDocInfoRow('ใช้ได้ถึง', moment(document.expiryDate).format('DD/MM/YYYY'))
            )}
            {document.type !== IncomeDocumentTypes.BILLING && (
              _renderDocInfoRow(
                'อ้างอิง',
                [document?.linkingDocument, ...(document?.childrenDocumentsJson || [])]
                  .filter(Boolean)
                  .map((doc) => doc.code)
                  .join(', ') || '-'
              )
            )}
            {document?.taxCode && _renderDocInfoRow('เลขที่ใบกำกับภาษี', document.taxCode)}
            {document?.invoiceDate && _renderDocInfoRow('วันที่ใบกํากับภาษี', document.invoiceDate)}
          </View>
          {isExpenseDocument ? (
            <View style={{marginTop: '8px'}}>
              <Text style={{fontWeight: 'bold', width: 70}}>{`ผู้จัดทำ :`}</Text>
              <View style={{display: 'flex', flexDirection: "column", gap: '2px', marginTop: '4px',}}>
                {_renderIconLabel('\uf007', document?.actionBy?.fullName || '-')}
              </View>
            </View>
          ) : (
          <View style={{marginTop: '8px'}}>
            <Text style={{fontWeight: 'bold', width: 70}}>{`พนักงานขาย :`}</Text>
            <View style={{display: 'flex', flexDirection: "column", gap: '2px', marginTop: '4px',}}>
              {/* TODO:: Mock data */}
              {_renderIconLabel('\uf007', seller?.fullName || '-')}
              {_renderIconLabel('\uf095', seller?.mobile || '-')}
              {_renderIconLabel('\uf0e0', seller?.email || '-')}
            </View>
          </View>
          )}
        </View>
      )
    }

    return (
      <View style={{display: 'flex', flexDirection: "row", gap: '1rem'}}>
        <LeftSide />
        <RightSide />
      </View>
    )
  }

  return (
    <Document title={`LDC-${document.code}`}>
      {totalPageList.map((_, index) => {
        return (
          <Page key={`doc-${document.code}-${index}`} size='A4' style={styles.page}>
            <DocHeadingSection isOriginal={index === 0} />
            <DocContactSection />
            <PrintProductSection document={document} />
            <PrintSummarySection document={document} productPriceInfo={productPriceInfo} />
            {shouldRenderPayment && <PrintPaymentSection document={document} />}
            <PrintPsSection document={document} />
            <PrintSignatureSection document={document} />
          </Page>
        )
      })}
    </Document>
  )
}

export default IncomeDocumentPDFPage;
